import { useEffect, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Layout from "@/components/layout/Layout";
import Index from "./pages/Index";

const Services = lazy(() => import("./pages/Services"));
const UseCases = lazy(() => import("./pages/UseCases"));
const Events = lazy(() => import("./pages/Events"));
const Book = lazy(() => import("./pages/Book"));
const Contact = lazy(() => import("./pages/Contact"));
const Legal = lazy(() => import("./pages/Legal"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const UnsubscribeConfirmed = lazy(() => import("./pages/UnsubscribeConfirmed"));
const Alfred = lazy(() => import("./pages/Alfred"));

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempts++ < 10) {
          setTimeout(tryScroll, 50);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash, key]);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/use-cases" element={<UseCases />} />
              <Route path="/events" element={<Events />} />
              <Route path="/book" element={<Book />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Legal kind="privacy" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/cookies" element={<Legal kind="cookies" />} />
              <Route path="/thank-you-diagnosis" element={<ThankYou kind="diagnosis" />} />
              <Route path="/thank-you-webinar" element={<ThankYou kind="webinar" />} />
              <Route path="/thank-you-contact" element={<ThankYou kind="contact" />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/unsubscribe-confirmed" element={<UnsubscribeConfirmed />} />
              <Route path="/alfred" element={<Alfred />} />
              <Route path="/built" element={<Navigate to="/#apps" replace />} />
              <Route path="/workflow-diagnosis" element={<Navigate to="/book" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Layout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
